'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Nanoplastics Size vs Microplastics: How Small Is Nano, Really?"
      author="Claire Rowan"
      date="June 5, 2026"
      readTime="10 min read"
      heroImage="/blog/heroes/general.webp"
      heroImageAlt="Nanoplastics size compared to microplastics, viruses, red blood cells and human hair"
    >
      <BlogLastUpdated iso="2026-06-05" formatted="June 5, 2026" />

      <BlogTLDR>
        Microplastics are particles between <strong>1 µm and 5 mm</strong>.
        Nanoplastics are particles below <strong>1 µm (1,000 nm)</strong>, and many working groups use <strong>100 nm</strong> as a stricter
        cutoff to match the broader nanomaterial science definition.
        For scale: a human hair is ~70,000 nm wide, a red blood cell is
        ~7,000 nm, a typical bacterium is ~1,000 nm, a SARS-CoV-2 virion
        is ~100 nm. Nanoplastics overlap virus-sized range and can cross
        biological barriers, cell membranes, the placenta, the
        blood-brain barrier, that microplastics cannot. That is why
        nano is the part of the size distribution toxicologists are most
        concerned about, even though micro is what we can currently
        measure most reliably.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Microplastic = 1 µm to 5 mm. Nanoplastic = under 1 µm. Some agencies use under 100 nm for nano to match nanomaterial regulation.</>,
          <>1 micrometre (µm) = 1,000 nanometres (nm). Below 1 µm is the boundary where particles begin to cross intact biological membranes.</>,
          <>A human hair is ~70 µm (70,000 nm) across, roughly 70× wider than a 1 µm cutoff and 700× wider than a 100 nm cutoff.</>,
          <>Nanoplastics are in the same size range as viruses and large proteins. This is the size scale at which they can enter cells, cross the placenta, and likely reach the brain.</>,
          <>Most published microplastic counts in food and water are micrometre-scale because instruments routinely under-detect nanoparticles. The actual nanoplastic count is likely orders of magnitude higher than the micro count.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The exact size definitions (and where they disagree)
      </h2>
      <p>
        There isn&apos;t one universally agreed nanoplastic size cutoff.
        The two definitions you&apos;ll see in the literature:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>The 1 µm cutoff.</strong> Used by most marine and food microplastic researchers, by GESAMP (the UN Joint Group of Experts on the Scientific Aspects of Marine Environmental Protection), and by NOAA in its working definitions. A nanoplastic is anything below 1 µm.</li>
        <li><strong>The 100 nm cutoff.</strong> Used by ECHA (European Chemicals Agency) and aligns with the broader EU definition of a &ldquo;nanomaterial.&rdquo; A nanoplastic is anything below 100 nm.</li>
      </ul>
      <p>
        Both are defensible. The 1 µm cutoff is more practically useful
        because it marks the boundary where particles begin to behave
        differently in biology, crossing membranes, getting taken up by
        cells, evading the size-based filtration of the gut wall. The 100
        nm cutoff is stricter and matches how regulators think about
        engineered nanomaterials.
      </p>
      <p>
        Microplastics are easier, the field has converged on{' '}
        <strong>1 µm to 5 mm</strong> as the consensus range. Above 5 mm
        is &ldquo;macroplastic&rdquo;; below 1 µm crosses into the
        nanoplastic category whichever cutoff you use.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Comparing nano to things you can picture
      </h2>

      <BlogComparisonTable
        caption="Size scale comparison: nanoplastics, microplastics, and familiar reference points"
        columns={[
          { key: 'item', label: 'Item' },
          { key: 'size', label: 'Approximate size' },
          { key: 'category', label: 'Category' },
        ]}
        rows={[
          { item: 'Grain of rice', size: '~5 mm', category: 'Above microplastic range (macroplastic boundary)' },
          { item: 'Coarse sand grain', size: '~500 µm (0.5 mm)', category: 'Microplastic, large end' },
          { item: 'Human hair (cross-section)', size: '~70 µm', category: 'Microplastic, easily visible under light microscope' },
          { item: 'Red blood cell', size: '~7 µm', category: 'Microplastic, small end' },
          { item: 'Bacterium (E. coli)', size: '~1 µm', category: 'Boundary, micro/nano cutoff' },
          { item: 'Largest virus (mimivirus)', size: '~500 nm (0.5 µm)', category: 'Nanoplastic range' },
          { item: 'SARS-CoV-2 virion', size: '~100 nm', category: 'Nanoplastic. ECHA cutoff boundary' },
          { item: 'Antibody (IgG)', size: '~10 nm', category: 'Nanoplastic, small end' },
          { item: 'Water molecule', size: '~0.3 nm', category: 'Far below nanoplastic range' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why size matters biologically
      </h2>
      <p>
        The size of a particle determines which biological barriers it can
        cross. Three barriers matter most:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>The gut wall.</strong> Particles above ~150 µm pass through. Particles between 1 µm and 150 µm largely stay in the gut lumen, with some uptake into the lymphatic Peyer&apos;s patches. Particles below 1 µm (nanoplastics) can be taken up across the intestinal epithelium and enter circulation.</li>
        <li><strong>The placenta.</strong> Ragusa et al. (2021), publishing in <em>Environment International</em>, found microplastics in human placental tissue at sizes down to 5 µm. Smaller nanoplastics likely cross more easily, although direct detection has been limited by instrument sensitivity.</li>
        <li><strong>The blood-brain barrier.</strong> Animal studies (including the 2024 University of New Mexico work by Campen and colleagues) have detected microplastics in brain tissue. The accumulation is hypothesised to be dominated by the nanoplastic fraction, which can cross the BBB&apos;s tight junctions, whereas micrometre-scale particles cannot.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What human-tissue studies actually measured
      </h2>
      <p>
        The headline human-tissue studies of the last few years, often-quoted as &ldquo;microplastics found in blood / placenta /
        plaque / breast milk&rdquo;, typically detected particles in the
        low-micrometre range, not the nanometre range. This isn&apos;t
        because nanoplastics aren&apos;t there. It&apos;s because the
        detection methods used (FTIR, micro-Raman, scanning electron
        microscopy) have a practical lower limit of about 700 nm to 1 µm.
        Below that, instruments stop reliably identifying polymer
        chemistry.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Leslie et al. (2022), human blood.</strong> Detected particles down to ~700 nm using py-GC/MS. Found microplastics in 77% of 22 healthy donors.</li>
        <li><strong>Ragusa et al. (2021), human placenta.</strong> Detected particles 5–10 µm using Raman.</li>
        <li><strong>Ragusa et al. (2022), human breast milk.</strong> Detected particles 2–12 µm using Raman.</li>
        <li><strong>Marfella et al. (2024), NEJM, carotid plaque.</strong> Detected particles 1–10 µm using pyrolysis-GC/MS and electron microscopy. Patients with plaque microplastics had a 4.53× higher risk of MACE (heart attack, stroke, or death) over 34 months.</li>
      </ul>
      <p>
        The implication: when you see a count like &ldquo;microplastics
        detected in blood,&rdquo; the actual particle burden is likely
        much higher than the published number, because the
        nano-fraction below detection is invisible to the instrument.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why nanoplastics are likely more harmful than microplastics
      </h2>
      <p>
        Three mechanisms drive the toxicological concern around nano vs micro:
      </p>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>They cross biological barriers.</strong> Micrometre-scale particles are mostly trapped by the gut wall, the placenta, and the blood-brain barrier. Nanoplastics are not.</li>
        <li><strong>They have much higher surface area per unit mass.</strong> Surface area is where chemical migrants (BPA, phthalates, PFAS) adsorb and where reactive sites interact with cells. A given mass of plastic broken down into nanoparticles has thousands of times the surface area of the same mass as one large piece.</li>
        <li><strong>They are taken up by cells.</strong> Cell-uptake mechanisms (endocytosis) are size-dependent. Nanoparticles in the 50–500 nm range are particularly efficient at being internalised by cells, including immune cells, where they can trigger inflammatory cascades.</li>
      </ol>
      <p>
        The blunt summary: micrometre-scale microplastics are mostly an
        exposure marker. Nanoplastics are the fraction that toxicologists
        believe is doing the actual biological damage.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The detection gap, and why it matters for the news you read
      </h2>
      <p>
        Most published microplastic counts in food, water, and tissue are
        undercounts. The reason: the methods used (FTIR, Raman, SEM-EDS,
        py-GC/MS) trade off between sensitivity and chemistry
        identification, and almost all of them lose reliability below
        ~700 nm to 1 µm. So when a study reports &ldquo;5 particles per
        litre of tap water,&rdquo; the nanoplastic fraction below the
        detection cutoff is excluded from that number.
      </p>
      <p>
        A 2024 PNAS study by Qian et al., using stimulated Raman
        scattering, which can resolve down to ~100 nm, reported that
        bottled water contains an average of <strong>240,000 plastic
        particles per litre</strong>, roughly 90% of which are
        nanoplastics. That&apos;s 100× higher than previous bottled-water
        estimates and is the closest single number we have to the
        &ldquo;true&rdquo; particle load including the nano fraction.
      </p>

      <p>
        See also our explainer{' '}
        <Link href="/blog/nanoplastics-vs-microplastics" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          nanoplastics vs microplastics
        </Link>{' '}
        for the broader comparison, and{' '}
        <Link href="/blog/microplastics-in-bottled-water" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in bottled water
        </Link>{' '}
        for the Qian 2024 PNAS findings in context.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Product packaging polymer, the dominant source of micro and nano release into the food or drink it touches.</>,
          <>Heat-contact, pressure, and acidity context that accelerate nano-scale particle release.</>,
          <>Whether the underlying research on the product category includes nano-scale measurement, not just micro counts.</>,
          <>A 0–100 risk score that weighs nano-release likelihood, not only micro count where data exists.</>,
        ]}
      />

      <BlogAppCTA
        headline="Find the products in your home contributing most to nano exposure"
        sub="Scan packaging with the MicroPlastics app to see the polymer, the use-condition risk, and the cleaner same-format alternative, for the items most likely to break down into the nano fraction."
        buttonLabel="Check a product"
      />

      <BlogFAQ
        items={[
          {
            question: 'What is the size difference between nanoplastics and microplastics?',
            answer:
              'Microplastics are between 1 micrometre and 5 millimetres. Nanoplastics are smaller than 1 micrometre (1,000 nanometres). Some agencies use a stricter cutoff of 100 nanometres for nano. 1 micrometre equals 1,000 nanometres.',
          },
          {
            question: 'How small is a nanoplastic compared to a human hair?',
            answer:
              'A human hair is about 70 micrometres (70,000 nanometres) across. A nanoplastic at the 1 micrometre cutoff is about 70 times thinner than a human hair. A nanoplastic at the 100 nanometre cutoff is about 700 times thinner.',
          },
          {
            question: 'How small is a nanoplastic compared to a virus?',
            answer:
              'They overlap. SARS-CoV-2 is about 100 nanometres across, which is at the ECHA nanoplastic boundary. Larger viruses like mimivirus reach 500 nm, well inside the nanoplastic range. Nanoplastics are in the size class where particles begin to interact with cells the way viruses do.',
          },
          {
            question: 'Why are nanoplastics more dangerous than microplastics?',
            answer:
              'Three reasons. First, they cross biological barriers (gut wall, placenta, blood-brain barrier) that micrometre particles cannot. Second, they have far higher surface area per unit mass, which carries more adsorbed chemicals. Third, they are efficiently internalised by cells, including immune cells, where they can trigger inflammation.',
          },
          {
            question: 'Can nanoplastics cross the blood-brain barrier?',
            answer:
              'Animal studies suggest yes, and 2024 human-brain microplastic detection is consistent with that. The blood-brain barrier filters particles by size, and nanoplastics are at or below the cutoff where it stops being effective. Direct human evidence of trans-BBB nanoplastic transport is still being developed.',
          },
          {
            question: 'How are nanoplastics detected?',
            answer:
              'Conventional methods (FTIR, Raman, SEM) lose reliability below 700 nm to 1 µm. Newer techniques like stimulated Raman scattering (SRS) and pyrolysis-GC/MS reach the 100 nm scale. The Qian et al. 2024 PNAS study used SRS to find ~240,000 plastic particles per litre in bottled water, 90% of them nanoplastics.',
          },
          {
            question: 'Do microplastic counts in food and water include nanoplastics?',
            answer:
              'Usually not. Most published counts come from methods with a 1 µm detection floor, so the nano-fraction is excluded. The true particle load including nanoplastics is likely orders of magnitude higher than the published micrometre-only counts.',
          },
          {
            question: 'Are nanoplastics found in human blood?',
            answer:
              'Yes: Leslie et al. (2022) in Environment International detected polymer particles down to about 700 nm in human blood, which is the small end of microplastic / large end of nanoplastic. Methods sensitive to the full nanoplastic range have not yet been widely applied to human blood samples.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Qian N, Gao X, Lang X, et al.',
            year: 2024,
            title: 'Rapid single-particle chemical imaging of nanoplastics by SRS microscopy',
            publisher: 'Proceedings of the National Academy of Sciences (PNAS)',
            url: 'https://www.pnas.org/doi/10.1073/pnas.2300582121',
          },
          {
            authors: 'Leslie HA, van Velzen MJM, Brandsma SH, et al.',
            year: 2022,
            title: 'Discovery and quantification of plastic particle pollution in human blood',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412022001258',
          },
          {
            authors: 'Marfella R, Prattichizzo F, Sardu C, et al.',
            year: 2024,
            title: 'Microplastics and nanoplastics in atheromas and cardiovascular events',
            publisher: 'New England Journal of Medicine',
            url: 'https://www.nejm.org/doi/full/10.1056/NEJMoa2309822',
          },
          {
            authors: 'Ragusa A, Svelato A, Santacroce C, et al.',
            year: 2021,
            title: 'Plasticenta: First evidence of microplastics in human placenta',
            publisher: 'Environment International',
            url: 'https://www.sciencedirect.com/science/article/pii/S0160412020322297',
          },
          {
            authors: 'GESAMP',
            year: 2019,
            title: 'Guidelines for the monitoring and assessment of plastic litter in the ocean',
            publisher: 'IMO/FAO/UNESCO-IOC/UNIDO/WMO/IAEA/UN/UNEP/UNDP Joint Group of Experts',
            url: 'http://www.gesamp.org/publications/guidelines-for-the-monitoring-and-assessment-of-plastic-litter-in-the-ocean',
          },
          {
            authors: 'European Chemicals Agency',
            year: 2023,
            title: 'Definition of nanomaterial: Commission Recommendation (EU) 2022/C 229/01',
            publisher: 'ECHA',
            url: 'https://echa.europa.eu/regulations/nanomaterials',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
